import React, { useState } from 'react';
import Cookies from "js-cookie";
import axios from "axios";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Tag,
    X,
    Plus,
    Clock,
    Loader2,
} from "lucide-react";

import { BACKEND_API_URL } from "../utils/api";
const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const TagInputDialog = ({ open, onClose, onSave, existingTags, id, type, recentTags, setRecentTags }) => {
    const [newTag, setNewTag] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Generate consistent colors for tags based on their name
    const getTagColor = (tagName) => {
        const colors = [
            "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/25 hover:bg-blue-500/25",
            "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/25",
            "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/25 hover:bg-violet-500/25",
            "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/25 hover:bg-amber-500/25",
            "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/25 hover:bg-rose-500/25",
            "bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/25 hover:bg-cyan-500/25",
            "bg-pink-500/15 text-pink-700 dark:text-pink-400 border-pink-500/25 hover:bg-pink-500/25",
            "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/25 hover:bg-orange-500/25",
        ];
        const hash = String(tagName).split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    };

    const handleAddTag = async (tag = null) => {
        const tagToAdd = tag || newTag.trim();
        if (!tagToAdd) return;

        setIsLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/${type}/${id}`, {
                tag: tagToAdd,
            }, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            const updatedTags = response.data.data.tags;
            const recent_tags = response.data.recent_tags;
            setRecentTags(recent_tags);
            onSave(updatedTags);
            toast.success("Tag added successfully");
            setNewTag('');
        } catch (error) {
            toast.error("Error adding tag");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTag = async (tagToDelete) => {
        const deleteType = "remove_tag_from_item";
        setIsLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/${deleteType}/${id}/tag/${tagToDelete}`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            const updatedTags = response.data.tags;
            onSave(updatedTags);
            toast.success("Tag removed successfully");
        } catch (error) {
            toast.error("Error removing tag");
        } finally {
            setIsLoading(false);
        }
    };

    // Filter out recent tags that already exist
    const availableRecentTags = recentTags?.filter(
        (tag) => !existingTags?.some((existing) => existing.name === tag)
    ) || [];

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="flex items-center justify-center size-8 rounded-lg bg-violet-500/10">
                            <Tag className="size-4 text-violet-600" />
                        </div>
                        Manage Tags
                    </DialogTitle>
                    <DialogDescription>
                        Add or remove tags to organize your items
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Current Tags */}
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                            Current Tags
                        </Label>
                        <div className="min-h-[40px]">
                            {existingTags && existingTags.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {existingTags.map((tag, index) => (
                                        <Badge
                                            key={index}
                                            variant="outline"
                                            className={cn(
                                                "text-xs font-medium pl-2.5 pr-1.5 py-1 gap-1.5 transition-colors",
                                                getTagColor(tag.name)
                                            )}
                                        >
                                            {tag.name}
                                            <button
                                                onClick={() => handleDeleteTag(tag.id)}
                                                disabled={isLoading}
                                                className="size-4 rounded-full flex items-center justify-center hover:bg-destructive/20 text-current hover:text-destructive transition-colors"
                                            >
                                                <X className="size-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-[40px] rounded-lg border border-dashed border-border/60 bg-muted/30">
                                    <p className="text-xs text-muted-foreground">No tags yet</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <Separator />

                    {/* Recent Tags */}
                    {availableRecentTags.length > 0 && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5">
                                <Clock className="size-3 text-muted-foreground" />
                                <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                                    Recent Tags
                                </Label>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {availableRecentTags.map((tag, index) => (
                                    <Badge
                                        key={index}
                                        variant="outline"
                                        onClick={() => handleAddTag(tag)}
                                        className={cn(
                                            "text-xs font-medium px-2.5 py-1 cursor-pointer transition-all",
                                            "bg-muted/50 text-muted-foreground border-border/50",
                                            "hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 hover:scale-105",
                                            isLoading && "pointer-events-none opacity-50"
                                        )}
                                    >
                                        <Plus className="size-3 mr-1" />
                                        {tag}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add New Tag */}
                    <div className="space-y-2">
                        <Label htmlFor="new-tag" className="text-xs text-muted-foreground uppercase tracking-wider">
                            Add New Tag
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                id="new-tag"
                                placeholder="Enter tag name..."
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                                disabled={isLoading}
                                className="flex-1"
                            />
                            <Button
                                onClick={() => handleAddTag()}
                                disabled={!newTag.trim() || isLoading}
                                size="default"
                                className="gap-1.5"
                            >
                                {isLoading ? (
                                    <Loader2 className="size-4 animate-spin" />
                                ) : (
                                    <Plus className="size-4" />
                                )}
                                Add
                            </Button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TagInputDialog;
